import {useState} from 'react';

import {Box, ButtonBase, InputBase, Stack, Typography} from '@mui/material';

import ShopifyMappingEditor from './ShopifyMappingEditor';

const C = {
    ink: 'var(--cg-text)',
    surface: 'var(--cg-surface)',
    surfaceAlt: 'var(--cg-surface-alt)',
    border: 'var(--cg-border)',
    border2: 'var(--cg-border-strong)',
    textSoft: 'var(--cg-text-soft)',
    textMuted: 'var(--cg-text-muted)',
    teal: '#1D9E75',
    tealL: '#E1F5EE',
    amber: '#BA7517',
    amberL: '#FAEEDA',
    blue: '#185FA5',
    blueL: '#E6F1FB',
    purple: '#534AB7',
    purpleL: '#EEEDFE',
};

const POV_OPTIONS = [
    {value: '1st', label: '1st person (we, our)'},
    {value: '2nd', label: '2nd person (you, your)'},
    {value: '3rd', label: '3rd person (the brand)'},
];

function SectionHeader({children, color = C.textSoft}) {
    return (
        <Typography sx={{
            fontSize: 11,
            fontWeight: 700,
            color,
            textTransform: 'uppercase',
            letterSpacing: '.05em',
            marginBottom: '8px',
            marginTop: '20px',
            '&:first-of-type': {marginTop: 0},
        }}>
            {children}
        </Typography>
    );
}

function FieldLabel({children}) {
    return (
        <Typography sx={{fontSize: 12, fontWeight: 600, color: C.textSoft, marginBottom: '4px'}}>
            {children}
        </Typography>
    );
}

function TextInput({value, onChange, placeholder, multiline = false, rows = 1}) {
    return (
        <InputBase
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            multiline={multiline}
            rows={multiline ? rows : undefined}
            sx={{
                width: '100%',
                fontFamily: 'inherit',
                fontSize: 13,
                padding: '9px 12px',
                borderRadius: '10px',
                border: `1px solid ${C.border2}`,
                background: C.surfaceAlt,
                '& textarea': {resize: 'vertical'},
            }}
        />
    );
}

function TagEditor({tags, onChange, placeholder}) {
    const [input, setInput] = useState('');

    const addTag = () => {
        const trimmed = input.trim();
        if (!trimmed || tags.includes(trimmed)) return;
        onChange([...tags, trimmed]);
        setInput('');
    };

    const removeTag = (index) => {
        onChange(tags.filter((_, i) => i !== index));
    };

    return (
        <Box>
            <Stack direction="row" gap="6px" flexWrap="wrap" sx={{marginBottom: tags.length ? '6px' : 0}}>
                {tags.map((tag, i) => (
                    <Box key={`${tag}-${i}`} sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '3px 8px',
                        borderRadius: 999,
                        background: C.surfaceAlt,
                        border: `1px solid ${C.border}`,
                        fontSize: 12,
                        fontWeight: 500,
                        color: C.ink,
                    }}>
                        {tag}
                        <ButtonBase
                            onClick={() => removeTag(i)}
                            sx={{fontSize: 14, color: C.textMuted, cursor: 'pointer', lineHeight: 1, marginLeft: '2px'}}>
                            x
                        </ButtonBase>
                    </Box>
                ))}
            </Stack>
            <InputBase
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addTag();
                    }
                }}
                onBlur={addTag}
                placeholder={placeholder}
                sx={{
                    width: '100%',
                    fontFamily: 'inherit',
                    fontSize: 12,
                    padding: '7px 10px',
                    borderRadius: '8px',
                    border: `1px solid ${C.border}`,
                    background: 'transparent',
                }}
            />
        </Box>
    );
}

export default function BrandProfileEditor({profile, onChange}) {
    const update = (key, value) => onChange({...profile, [key]: value});

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            {/* Identity */}
            <SectionHeader color={C.teal}>Brand identity</SectionHeader>
            <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                <Box>
                    <FieldLabel>Company name</FieldLabel>
                    <TextInput value={profile.companyName} onChange={(v) => update('companyName', v)} placeholder="Brand name" />
                </Box>
                <Box>
                    <FieldLabel>Website</FieldLabel>
                    <TextInput value={profile.websiteUrl} onChange={(v) => update('websiteUrl', v)} placeholder="https://..." />
                </Box>
            </Box>
            <Box>
                <FieldLabel>Tagline / positioning</FieldLabel>
                <TextInput value={profile.tagline} onChange={(v) => update('tagline', v)} placeholder="Your brand positioning statement" />
            </Box>
            <Box>
                <FieldLabel>Personality traits</FieldLabel>
                <TagEditor tags={profile.personality || []} onChange={(v) => update('personality', v)} placeholder="Add trait (e.g. refined, confident)" />
            </Box>
            <Box>
                <FieldLabel>Target audience</FieldLabel>
                <TextInput value={profile.targetAudience} onChange={(v) => update('targetAudience', v)} placeholder="e.g. homeowners, interior designers, contractors" />
            </Box>

            {/* Voice & Tone */}
            <SectionHeader color={C.blue}>Voice & tone</SectionHeader>
            <Box>
                <FieldLabel>Writing style</FieldLabel>
                <TextInput value={profile.voiceStyle} onChange={(v) => update('voiceStyle', v)} placeholder="e.g. Professional, precise, design-forward" />
            </Box>
            <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                <Box>
                    <FieldLabel>Tone tags</FieldLabel>
                    <TagEditor tags={profile.toneTags || []} onChange={(v) => update('toneTags', v)} placeholder="Add tone (e.g. warm)" />
                </Box>
                <Box>
                    <FieldLabel>Point of view</FieldLabel>
                    <Box
                        component="select"
                        value={profile.personPov || '2nd'}
                        onChange={(e) => update('personPov', e.target.value)}
                        sx={{
                            width: '100%',
                            fontFamily: 'inherit',
                            fontSize: 13,
                            padding: '10px 12px',
                            borderRadius: '10px',
                            border: `1px solid ${C.border2}`,
                            background: C.surfaceAlt,
                            cursor: 'pointer',
                        }}>
                        {POV_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </Box>
                </Box>
            </Box>
            <Box>
                <FieldLabel>Words to use</FieldLabel>
                <TagEditor tags={profile.wordsLove || []} onChange={(v) => update('wordsLove', v)} placeholder="Preferred vocabulary" />
            </Box>
            <Box>
                <FieldLabel>Words to avoid</FieldLabel>
                <TagEditor tags={profile.wordsAvoid || []} onChange={(v) => update('wordsAvoid', v)} placeholder="Off-brand vocabulary" />
            </Box>

            {/* SEO & Keywords */}
            <SectionHeader color={C.amber}>Keywords</SectionHeader>
            <Box>
                <FieldLabel>Primary keywords</FieldLabel>
                <TagEditor tags={profile.primaryKeywords || []} onChange={(v) => update('primaryKeywords', v)} placeholder="Main ranking targets" />
            </Box>
            <Box>
                <FieldLabel>Secondary keywords</FieldLabel>
                <TagEditor tags={profile.secondaryKeywords || []} onChange={(v) => update('secondaryKeywords', v)} placeholder="Long-tail / supporting keywords" />
            </Box>

            {/* Knowledge */}
            <SectionHeader color={C.purple}>Product knowledge & rules</SectionHeader>
            <Box>
                <FieldLabel>Product knowledge</FieldLabel>
                <TextInput
                    value={profile.productKnowledge}
                    onChange={(v) => update('productKnowledge', v)}
                    placeholder="Materials, certifications, quality claims, differentiators..."
                    multiline
                    rows={4}
                />
            </Box>
            <Box>
                <FieldLabel>Content rules</FieldLabel>
                <TextInput
                    value={profile.contentRules}
                    onChange={(v) => update('contentRules', v)}
                    placeholder="Legal restrictions, required disclaimers, style rules..."
                    multiline
                    rows={3}
                />
            </Box>

            {/* Shopify Mapping */}
            <SectionHeader color="#1D9E75">Shopify mapping</SectionHeader>
            <ShopifyMappingEditor
                mapping={profile.shopifyMapping}
                onChange={(v) => update('shopifyMapping', v)}
            />
        </Box>
    );
}
